import { matrizToNumber } from "../Produccion/a3PedidoMatrizUtils";

export const LOGISTICA_DOC_TYPES = [
  { id: "invoice", tipo: "Invoice", short: "Invoice" },
  { id: "packing", tipo: "Packing List", short: "Packing" },
  { id: "bl", tipo: "BL / AWB", short: "BL" },
  { id: "origen", tipo: "Certificado Origen", short: "Origen" },
  { id: "insurance", tipo: "Insurance", short: "Seguro" },
  { id: "otros", tipo: "Otros", short: "Otros" },
];

export function normalizeLogisticaDocTipo(tipo) {
  const s = String(tipo || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
  if (!s) return "otros";
  if (/(invoice|factura)/.test(s)) return "invoice";
  if (/(packing|\bpl\b)/.test(s)) return "packing";
  if (/(awb|conocimiento|bill of lading|\bbl\b|telex)/.test(s)) return "bl";
  if (/(origen|origin|\bcoo\b)/.test(s)) return "origen";
  if (/(insur|seguro)/.test(s)) return "insurance";
  return "otros";
}

export function buildLogisticaDocChecklist(documentos) {
  const docs = Array.isArray(documentos) ? documentos : [];
  const byType = new Map(LOGISTICA_DOC_TYPES.map((t) => [t.id, []]));
  for (const doc of docs) {
    const id = normalizeLogisticaDocTipo(doc?.TipoDocumento);
    if (!byType.has(id)) byType.set(id, []);
    byType.get(id).push(doc);
  }
  return LOGISTICA_DOC_TYPES.map((t) => {
    const list = byType.get(t.id) || [];
    return { ...t, docs: list, present: list.length > 0 };
  });
}

export function logisticaDocPublicUrl(archivo) {
  const api = String(process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const rel = String(archivo || "").replace(/^\/+/, "");
  if (!api || !rel) return "";
  return `${api}/uploads/logistica/${rel}`;
}

export function logisticaDocDisplayName(doc) {
  const named = String(doc?.NombreDocumento || doc?.nombreDocumento || "").trim();
  if (named) return named;
  const archivo = String(doc?.Archivo || "").replace(/\\/g, "/");
  return archivo.split("/").pop() || "";
}

export function fileKind(fileName = "") {
  const n = String(fileName).toLowerCase();
  if (/\.(jpe?g|png|gif|webp)$/.test(n)) return "image";
  if (/\.pdf$/.test(n)) return "pdf";
  return "file";
}

/** Filas API A3 matriz → forma compatible con lectura de líneas envío */
export function a3MatrizRowsToLineasEnvio(rows) {
  if (!Array.isArray(rows)) return [];
  return rows
    .filter((x) => matrizToNumber(x?.unidades) > 0)
    .map((x) => ({
      CodFamTallaH: String(x.codFamTallaH || "").trim(),
      CodTallaH: String(x.codTallaH || "").trim(),
      CodFamTallaV: String(x.codFamTallaV || "").trim(),
      CodTallaV: String(x.codTallaV || "").trim(),
      CodTallaHDesc: String(x.descTallaH || "").trim(),
      CodTallaVDesc: String(x.descTallaV || "").trim(),
      Unidades: matrizToNumber(x.unidades),
    }));
}

export function fmtLogisticaDate(v) {
  if (v == null || v === "") return "—";
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-ES");
}

export function sumUnitsFromEnvioLineas(lineas) {
  const arr = Array.isArray(lineas) ? lineas : [];
  const total = arr.reduce(
    (acc, l) => acc + (Number(l?.Unidades ?? l?.unidades) || 0),
    0,
  );
  return total > 0 ? total : null;
}

export function getPedidoClienteLabel(pedido) {
  return String(
    pedido?.ClienteNombre ||
      pedido?.NomCli ||
      pedido?.RazonSocial ||
      pedido?.CodCli ||
      "",
  ).trim();
}

export function getPedidoSupplierLabel(pedido) {
  return String(
    pedido?.ProveedorNombre ||
      pedido?.NomPro ||
      pedido?.SupplierName ||
      pedido?.CodPro ||
      "",
  ).trim();
}

export function normalizeSearchValue(value) {
  return String(value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

export function tokenizeSearch(value) {
  const normalized = normalizeSearchValue(value);
  return normalized ? normalized.split(/\s+/).filter(Boolean) : [];
}

export function matchesSearchTokens(values, tokens) {
  if (!tokens.length) return true;
  const haystack = normalizeSearchValue(
    Array.isArray(values) ? values.join(" ") : values,
  );
  if (!haystack) return false;
  return tokens.every((token) => haystack.includes(token));
}

export function getShipmentMethodType(shipmentMethod) {
  const raw = String(shipmentMethod || "")
    .trim()
    .toLowerCase();

  if (!raw) return "unknown";

  const hasTruck = /(truck|road|terrest|cami[oó]n|land)/i.test(raw);
  const hasAir = /(air|aere|a[eé]r|avio|flight|plane)/i.test(raw);
  const hasSea = /(sea|boat|ship|ocean|mar[ií]t|barco|vessel)/i.test(raw);
  const matches = [hasTruck, hasAir, hasSea].filter(Boolean).length;
  const isSeaAir =
    /(sea-air|sea air|sea\/air|air\/sea|air-sea)/i.test(raw) ||
    (hasSea && hasAir && !hasTruck);

  if (isSeaAir) return "seaAir";
  if (/(courier|mensaj)/i.test(raw)) return "courier";
  if (matches > 1 || /combin|multi|mixed|\+|\/|&/.test(raw)) return "combined";
  if (hasAir) return "air";
  if (hasSea) return "sea";
  if (hasTruck) return "truck";
  return "unknown";
}

export function inputDate(v) {
  if (v == null || v === "") return "";
  return String(v).slice(0, 10);
}

export function apiDateFromInput(s) {
  const t = String(s || "").trim();
  return t || null;
}

export function parseOptionalInt(s) {
  const n = parseInt(String(s || "").replace(/\s/g, ""), 10);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

export function parseOptionalDecimal(s) {
  const t = String(s ?? "")
    .replace(",", ".")
    .trim();
  if (t === "") return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

export function maxDateInput(values) {
  const keys = (values || [])
    .map((v) => String(v || "").slice(0, 10))
    .filter(Boolean)
    .sort();
  return keys.length ? keys[keys.length - 1] : "";
}

export function emptyEnvioHeader() {
  return {
    ETA: "",
    Forwarder: "",
    POReference: "",
    PODate: "",
    Factory: "",
    Remarks: "",
    LinkDocs: "",
    ShipmentMethod: "",
    NCartons: "",
    GrossWeightKgs: "",
    TotalVolumeCbm: "",
    BookingDone: "",
    InspectionDate: "",
    ExFD: "",
    ETDF: "",
    CutOff: "",
    ETD: "",
    DD: "",
    ShipmentCodeCustomer: "",
    BLAWBNr: "",
    DocumentsApproved: "",
    OriginalTelexDocsSemt: "",
  };
}

export function mergeConsolidacionCabecera(base, consolidacion) {
  if (!consolidacion?.productosConReferencia) return base;
  let shipmentMethodFallback = "";
  let factoryFallback = "";
  for (const row of consolidacion.porPedido || []) {
    const sm = row?.productionLogistics?.ShipmentMethod;
    if (!shipmentMethodFallback && sm && String(sm).trim()) {
      shipmentMethodFallback = String(sm).trim();
    }
    const factory = row?.productionLogistics?.Factory;
    if (!factoryFallback && factory && String(factory).trim()) {
      factoryFallback = String(factory).trim();
    }
    if (shipmentMethodFallback && factoryFallback) break;
  }
  return {
    ...base,
    NCartons:
      consolidacion.sumaNCartons != null
        ? String(Math.round(consolidacion.sumaNCartons))
        : base.NCartons,
    GrossWeightKgs:
      consolidacion.sumaGrossWeightKgs != null
        ? String(consolidacion.sumaGrossWeightKgs)
        : base.GrossWeightKgs,
    TotalVolumeCbm:
      consolidacion.sumaTotalVolumeCbm != null
        ? String(consolidacion.sumaTotalVolumeCbm)
        : base.TotalVolumeCbm,
    ETA: consolidacion.etaMasTarde || base.ETA,
    BookingDone: consolidacion.bookingMasTarde || base.BookingDone,
    InspectionDate: consolidacion.inspectionDateMasTarde || base.InspectionDate,
    ExFD: consolidacion.exfdMasTarde || base.ExFD,
    ETDF: consolidacion.etdfMasTarde || base.ETDF,
    CutOff: consolidacion.cutOffMasTarde || base.CutOff,
    ETD: consolidacion.etdMasTarde || base.ETD,
    DD: consolidacion.ddMasTarde || base.DD,
    DocumentsApproved:
      consolidacion.documentsApprovedMasTarde || base.DocumentsApproved,
    OriginalTelexDocsSemt:
      consolidacion.originalTelexMasTarde || base.OriginalTelexDocsSemt,
    ShipmentMethod: shipmentMethodFallback || base.ShipmentMethod,
    Factory: String(base.Factory || "").trim()
      ? base.Factory
      : factoryFallback || base.Factory,
  };
}

export function headerFromEnvio(e) {
  if (!e) return emptyEnvioHeader();
  return {
    ETA: inputDate(e.ETA),
    Forwarder: e.Forwarder ?? "",
    POReference: e.POReference ?? "",
    PODate: inputDate(e.PODate),
    Factory: e.Factory ?? "",
    Remarks: e.Remarks ?? "",
    LinkDocs: e.LinkDocs ?? "",
    ShipmentMethod: e.ShipmentMethod ?? "",
    NCartons: e.NCartons != null && e.NCartons !== "" ? String(e.NCartons) : "",
    GrossWeightKgs:
      e.GrossWeightKgs != null && e.GrossWeightKgs !== ""
        ? String(e.GrossWeightKgs)
        : "",
    TotalVolumeCbm:
      e.TotalVolumeCbm != null && e.TotalVolumeCbm !== ""
        ? String(e.TotalVolumeCbm)
        : "",
    BookingDone: inputDate(e.BookingDone),
    InspectionDate: inputDate(e.InspectionDate),
    ExFD: inputDate(e.ExFD),
    ETDF: inputDate(e.ETDF),
    CutOff: inputDate(e.CutOff),
    ETD: inputDate(e.ETD),
    DD: inputDate(e.DD),
    ShipmentCodeCustomer: e.ShipmentCodeCustomer ?? "",
    BLAWBNr: e.BLAWBNr ?? "",
    DocumentsApproved: inputDate(e.DocumentsApproved),
    OriginalTelexDocsSemt: inputDate(e.OriginalTelexDocsSemt),
  };
}

export const LOGISTICA_TREE_CONSOLIDADO_KEY = "__CONSOLIDADO__";
export const LOGISTICA_TREE_SIN_CLIENTE_KEY = "__SIN_CLIENTE__";
export const LOGISTICA_TREE_VARIOS_ARTICULOS_KEY = "__VARIOS_ARTICULOS__";
export const LOGISTICA_TREE_SIN_ARTICULO_KEY = "__SIN_ARTICULO__";

export function getEnvioClienteLabels(envio) {
  const links = Array.isArray(envio?.pedidoLinks) ? envio.pedidoLinks : [];
  return [
    ...new Set(
      links.map((lnk) => getPedidoClienteLabel(lnk?.pedidoA3)).filter(Boolean),
    ),
  ];
}

export function getLogisticaEnvioTreeLabel(envio) {
  const id = envio?.IdLogisticaEnvio ?? "—";
  const links = Array.isArray(envio?.pedidoLinks) ? envio.pedidoLinks : [];
  const pos = [
    ...new Set(
      links
        .map((lnk) => {
          const pedido = lnk?.pedidoA3;
          const num = pedido?.NumDocVenta ?? pedido?.IdPedv;
          return num != null && String(num).trim() ? String(num).trim() : "";
        })
        .filter(Boolean),
    ),
  ];
  const eta = fmtLogisticaDate(envio?.ETA);
  const parts = [`#${id}`];
  if (pos[0]) parts.push(pos[0]);
  if (eta !== "—") parts.push(eta);
  return parts.join(" · ");
}

export function getEnvioArticuloCodes(envio) {
  const links = Array.isArray(envio?.pedidoLinks) ? envio.pedidoLinks : [];
  return [
    ...new Set(
      links
        .map((lnk) => String(lnk?.pedidoA3?.CodArt ?? "").trim())
        .filter(Boolean),
    ),
  ];
}

export function getLogisticaEnvioArticuloKey(envio) {
  const arts = getEnvioArticuloCodes(envio);
  if (arts.length > 1) return LOGISTICA_TREE_VARIOS_ARTICULOS_KEY;
  if (arts.length === 1) return arts[0];
  return LOGISTICA_TREE_SIN_ARTICULO_KEY;
}

export function groupLogisticaEnviosByArticulo(envios, labels = {}) {
  const variosLabel = labels.variosArticulos || "Varios artículos";
  const sinLabel = labels.sinArticulo || "(sin código)";
  const groups = new Map();
  const ensure = (key, label) => {
    if (!groups.has(key)) groups.set(key, { key, label, envios: [] });
    return groups.get(key);
  };

  for (const envio of Array.isArray(envios) ? envios : []) {
    const key = getLogisticaEnvioArticuloKey(envio);
    if (key === LOGISTICA_TREE_VARIOS_ARTICULOS_KEY) {
      ensure(key, variosLabel).envios.push(envio);
    } else if (key === LOGISTICA_TREE_SIN_ARTICULO_KEY) {
      ensure(key, sinLabel).envios.push(envio);
    } else {
      ensure(key, key).envios.push(envio);
    }
  }

  return Array.from(groups.values()).sort((a, b) => {
    const rank = (key) => {
      if (key === LOGISTICA_TREE_VARIOS_ARTICULOS_KEY) return 2;
      if (key === LOGISTICA_TREE_SIN_ARTICULO_KEY) return 1;
      return 0;
    };
    const diff = rank(a.key) - rank(b.key);
    if (diff !== 0) return diff;
    return String(a.label).localeCompare(String(b.label), "es");
  });
}

export function getLogisticaEnvioPedidosCount(envio) {
  const links = Array.isArray(envio?.pedidoLinks) ? envio.pedidoLinks : [];
  return links.length;
}

export function getLogisticaPedidoLinkId(lnk) {
  const n = Number(
    lnk?.IdPresupuestoA3Pedido ?? lnk?.pedidoA3?.IdPresupuestoA3Pedido,
  );
  return Number.isFinite(n) && n > 0 ? n : null;
}

/** Pedido CRM en la forma que usa PedidoEnviosDialog (misma que Pedidos). */
export function pedidoRowFromLogisticaLink(lnk) {
  const p = lnk?.pedidoA3;
  if (!p) return null;
  const id = Number(p.IdPresupuestoA3Pedido || lnk?.IdPresupuestoA3Pedido);
  if (!Number.isFinite(id) || id <= 0) return null;
  const idProducto =
    Number(p.IdProducto) ||
    Number(p.ProductId) ||
    Number(p.presupuesto?.IdProducto) ||
    Number(p.presupuesto?.ProductId) ||
    null;
  return {
    ...p,
    IdPresupuestoA3Pedido: id,
    IdProducto: Number.isFinite(idProducto) && idProducto > 0 ? idProducto : null,
    Units:
      Number(p.Units) ||
      Number(p.presupuesto?.Units) ||
      undefined,
  };
}

export function presupuestoFromPedidoRow(row) {
  if (!row) return null;
  const idPresupuesto = Number(row.IdPresupuesto);
  const productId =
    Number(row.IdProducto) ||
    Number(row.ProductId) ||
    Number(row.presupuesto?.IdProducto) ||
    Number(row.presupuesto?.ProductId) ||
    null;
  return {
    IdPresupuesto:
      Number.isFinite(idPresupuesto) && idPresupuesto > 0
        ? idPresupuesto
        : null,
    ProductId: Number.isFinite(productId) && productId > 0 ? productId : null,
    Units:
      Number(row.Units) ||
      Number(row.UnidadesVenta) ||
      Number(row.UnidadesCompra) ||
      Number(row.presupuesto?.Units) ||
      undefined,
  };
}

export function getLogisticaPedidoChipLabel(lnk) {
  const pedido = lnk?.pedidoA3 || {};
  const idPed = getLogisticaPedidoLinkId(lnk);
  const ref = String(pedido.Referencia || "").trim();
  const po = String(pedido.NumDocVenta ?? pedido.IdPedv ?? "").trim();
  const extra = ref || po;
  if (idPed && extra) return `#${idPed} · ${extra}`;
  if (idPed) return `#${idPed}`;
  return extra || "Pedido";
}

export function getLogisticaEnvioMergeLabel(envio) {
  const id = Number(envio?.IdLogisticaEnvio);
  const prefix = Number.isFinite(id) && id > 0 ? `#${id}` : "#—";
  const links = Array.isArray(envio?.pedidoLinks) ? envio.pedidoLinks : [];
  const refs = [
    ...new Set(
      links
        .map((l) => String(l?.pedidoA3?.Referencia || "").trim())
        .filter(Boolean),
    ),
  ];
  return refs.length ? `${prefix} · ${refs.join(", ")}` : prefix;
}

export function enviosAjenosDePedido(envios, idPedido, idEnvioActual) {
  const idPed = Number(idPedido);
  const idActual = Number(idEnvioActual);
  if (!Number.isFinite(idPed) || idPed <= 0) return [];
  return (Array.isArray(envios) ? envios : []).filter((e) => {
    const idE = Number(e?.IdLogisticaEnvio);
    if (!Number.isFinite(idE) || idE <= 0) return false;
    if (Number.isFinite(idActual) && idE === idActual) return false;
    if (e?.IsClosed) return false;
    const links = Array.isArray(e?.pedidoLinks) ? e.pedidoLinks : [];
    return links.some(
      (l) =>
        Number(
          l?.IdPresupuestoA3Pedido ?? l?.pedidoA3?.IdPresupuestoA3Pedido,
        ) === idPed,
    );
  });
}

export function groupLogisticaEnviosByCliente(envios, labels = {}) {
  const consolidadoLabel = labels.consolidado || "Consolidado";
  const sinClienteLabel = labels.sinCliente || "Sin cliente";
  const groups = new Map();
  const ensure = (key, label) => {
    if (!groups.has(key)) groups.set(key, { key, label, envios: [] });
    return groups.get(key);
  };

  for (const envio of Array.isArray(envios) ? envios : []) {
    const clientes = getEnvioClienteLabels(envio);
    if (clientes.length > 1) {
      ensure(LOGISTICA_TREE_CONSOLIDADO_KEY, consolidadoLabel).envios.push(
        envio,
      );
      continue;
    }
    if (clientes.length === 1) {
      ensure(clientes[0], clientes[0]).envios.push(envio);
      continue;
    }
    const fallback = String(envio?.ShipmentCodeCustomer || "").trim();
    if (fallback) {
      ensure(fallback, fallback).envios.push(envio);
    } else {
      ensure(LOGISTICA_TREE_SIN_CLIENTE_KEY, sinClienteLabel).envios.push(
        envio,
      );
    }
  }

  return Array.from(groups.values())
    .sort((a, b) => {
      const rank = (key) => {
        if (key === LOGISTICA_TREE_CONSOLIDADO_KEY) return 2;
        if (key === LOGISTICA_TREE_SIN_CLIENTE_KEY) return 1;
        return 0;
      };
      const diff = rank(a.key) - rank(b.key);
      if (diff !== 0) return diff;
      return String(a.label).localeCompare(String(b.label), "es");
    })
    .map((group) => ({
      ...group,
      articulos: groupLogisticaEnviosByArticulo(group.envios, labels),
    }));
}
